import { useRef, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getEventLPrices } from '../api';
import { eventPricingKeys } from './useEventPricing';

// Post-write L-price convergence for the Compare page.
//
// Why this exists: the L price (Skybox unitPrice) is the listing's price on
// Vivid Seats. A just-bought ($0) listing has no Vivid listing, and after its
// first B price Skybox publishes it asynchronously (up to ~2 min observed).
// The old code refetched once at +3 s, usually saw unitPrice 0, and that 0
// was then cached for 5 minutes server- and client-side. This hook instead
// polls the slim /lprices endpoint on a bounded backoff, patches ONLY the L
// fields of the tracked rows into the react-query cache, and stops as soon as
// Skybox reports the value (or the schedule runs out).
//
// Two kinds of tracking:
//  - 'write' / 'rearm': a priced row with NO L yet - done when unitPrice > 0.
//  - 'verify': a row that was already on Vivid when repriced. Skybox's what-if
//    is pinned for instant feedback, but it is a projection: Skybox's own UI
//    shows the same what-if and Vivid has been observed (2026-08-22) to lag or
//    drop rapid successive changes for UI and API writes alike. Verify polls
//    until Vivid's real L moves (or matches the what-if); if it never does
//    within the window the row goes to `staleIds` so the page can say so
//    instead of silently showing a price Vivid doesn't have.
//
// One session per event, shared by every tracked row: a bulk apply of 40
// rows and ten SETs in a row cost the same handful of calls. The server
// dedups concurrent pollers of one event (maxAge floor + in-flight sharing).

// Backoff after a write on a row with no L yet (~4 min, <= 7 calls)
const WRITE_STEPS_MS = [5000, 10000, 20000, 40000, 60000, 60000, 60000];
// Verification that Vivid took a new price on a listed row (~17 min, <= 8 calls)
const VERIFY_STEPS_MS = [10000, 20000, 30000, 60000, 120000, 180000, 300000, 300000];
// Shorter schedule for rows found pending on load / after a full refetch
// (someone else's write, bulk, Dashboard edit, navigate-back) and for the
// one-off re-check of stale rows on Refresh
const REARM_STEPS_MS = [5000, 15000, 40000, 90000];
const MAX_CONSECUTIVE_ERRORS = 3;
const POLL_MAX_AGE_MS = 3000;
const PRICE_EPS = 0.005;
// Vivid's real L vs Skybox's what-if for the same B: same price when within $1
const WHATIF_TOLERANCE = 1;

const keyOf = (id) => String(id);

// Verify rule: Vivid took the new price when its L moved off the pre-write
// value, or landed on Skybox's what-if for the new B (covers a write whose L
// legitimately equals the old one, e.g. a price that Vivid rounds the same).
function vividTookPrice(fresh, p) {
  if (!(fresh.unitPrice > 0)) return false;
  if (!(p.previousUnitPrice > 0)) return true;
  if (Math.abs(fresh.unitPrice - p.previousUnitPrice) >= PRICE_EPS) return true;
  return p.whatIf > 0 && Math.abs(fresh.unitPrice - p.whatIf) < WHATIF_TOLERANCE;
}

export function useLPricePoll(eventId, { onTick } = {}) {
  const queryClient = useQueryClient();
  const onTickRef = useRef(onTick);
  onTickRef.current = onTick;

  // Mutable session state (render only reads pendingIds / staleIds / pins via overlay)
  const s = useRef({
    eventId: null,
    gen: 0,                 // bumped on eventId change / unmount: in-flight ticks for the old event are ignored
    pending: new Map(),     // key -> { expectedListPrice, since, origin, vividListingId, previousUnitPrice, whatIf }
    stale: new Map(),       // key -> { since, expectedListPrice, previousUnitPrice, whatIf } (verify window ran out)
    steps: WRITE_STEPS_MS,
    attempt: 0,
    timer: null,
    inFlight: false,
    errors: 0,
  });
  // Skybox what-if / confirmed L values pinned per row so a later full refetch
  // that still carries unitPrice 0 (Vivid not published yet) or the pre-write
  // L (Vivid not updated yet) cannot flip the cell back. key -> { unitPrice, listPrice }
  const pins = useRef(new Map());

  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [staleIds, setStaleIds] = useState(() => new Set());
  const syncPending = useCallback(() => {
    setPendingIds(new Set(s.current.pending.keys()));
  }, []);
  const syncStale = useCallback(() => {
    setStaleIds(new Set(s.current.stale.keys()));
  }, []);

  const clearTimer = () => {
    if (s.current.timer) { clearTimeout(s.current.timer); s.current.timer = null; }
  };

  // tick/schedule reference each other; keep tick in a ref so the callbacks stay stable
  const tickRef = useRef(null);
  const schedule = useCallback((delayMs) => {
    clearTimer();
    s.current.timer = setTimeout(() => {
      s.current.timer = null;
      if (tickRef.current) tickRef.current();
    }, delayMs);
  }, []);

  const markStaleAndStop = useCallback((ev) => {
    // Schedule exhausted / too many errors: let the next mount refetch, but do
    // NOT trigger a refetch now (that would re-cache a possibly-early 0).
    queryClient.invalidateQueries({ queryKey: eventPricingKeys.detail(ev), refetchType: 'none' });
  }, [queryClient]);

  const tick = useCallback(async () => {
    const st = s.current;
    if (st.pending.size === 0 || st.inFlight) return;
    const gen = st.gen;
    const ev = st.eventId;
    st.inFlight = true;
    st.attempt += 1;
    const attempt = st.attempt;
    const started = Date.now();
    const resolved = [];
    let errorMsg = null;
    let staleChanged = false;
    try {
      const res = await getEventLPrices(ev, POLL_MAX_AGE_MS);
      if (gen !== st.gen) return; // navigated away / event changed while in flight
      st.errors = 0;
      const fresh = new Map((res.data?.listings || []).map(r => [keyOf(r.id), r]));

      // Decide per tracked row. Only an L that belongs to the B we expect
      // counts; a payload still showing the pre-write B waits for the next tick.
      const updates = new Map(); // key -> fields to patch into the cached row
      for (const [key, p] of st.pending) {
        const f = fresh.get(key);
        if (!f) { st.pending.delete(key); continue; } // sold / removed - nothing to wait for
        const fields = {};
        if (f.vividListingId && f.vividListingId !== p.vividListingId) fields.vividListingId = f.vividListingId;
        if (typeof f.listed === 'boolean') fields.listed = f.listed;
        const priceMatches = p.expectedListPrice == null
          || Math.abs((Number(f.listPrice) || 0) - p.expectedListPrice) < PRICE_EPS;
        const confirmed = priceMatches && (p.origin === 'verify' ? vividTookPrice(f, p) : f.unitPrice > 0);
        if (confirmed) {
          fields.unitPrice = f.unitPrice;
          pins.current.set(key, { unitPrice: f.unitPrice, listPrice: Number(f.listPrice) });
          st.pending.delete(key);
          resolved.push({ id: key, unitPrice: f.unitPrice, afterMs: Date.now() - p.since, origin: p.origin });
        }
        if (Object.keys(fields).length) updates.set(key, fields);
      }

      // Stale rows clear as soon as a fresh read shows Vivid moved (or the B
      // changed under them, which makes the old warning moot)
      for (const [key, info] of st.stale) {
        const f = fresh.get(key);
        if (!f) { st.stale.delete(key); staleChanged = true; continue; }
        const priceMatches = Math.abs((Number(f.listPrice) || 0) - info.expectedListPrice) < PRICE_EPS;
        if (!priceMatches) { st.stale.delete(key); staleChanged = true; continue; }
        if (vividTookPrice(f, info)) {
          st.stale.delete(key); staleChanged = true;
          pins.current.set(key, { unitPrice: f.unitPrice, listPrice: Number(f.listPrice) });
          updates.set(key, { ...(updates.get(key) || {}), unitPrice: f.unitPrice });
        }
      }

      if (updates.size) {
        queryClient.setQueryData(eventPricingKeys.detail(ev), (prev) => {
          if (!prev?.myInventory) return prev;
          let changed = false;
          const myInventory = prev.myInventory.map(item => {
            const u = updates.get(keyOf(item.id));
            if (!u) return item;
            const next = { ...item };
            for (const [k, v] of Object.entries(u)) {
              if (k === 'unitPrice' && !(v > 0)) continue; // never downgrade a positive L to 0
              if (next[k] !== v) { next[k] = v; changed = true; }
            }
            return next;
          });
          return changed ? { ...prev, myInventory } : prev;
        });
      }
    } catch (err) {
      if (gen !== st.gen) return;
      st.errors += 1;
      errorMsg = err?.response?.data?.error || err?.message || 'poll failed';
    } finally {
      if (gen === st.gen) st.inFlight = false;
    }
    if (gen !== st.gen) return;

    if (onTickRef.current) {
      try {
        onTickRef.current({
          attempt, eventId: ev, durationMs: Date.now() - started,
          resolved, pending: st.pending.size, stale: st.stale.size, error: errorMsg,
        });
      } catch { /* logging must never break the poller */ }
    }
    syncPending();
    if (staleChanged) syncStale();

    if (st.pending.size === 0) return; // done
    if (st.errors >= MAX_CONSECUTIVE_ERRORS) {
      // Dead session token / Skybox down: stop hammering; a later Refresh or
      // a new write re-arms us. Nothing is marked stale - we simply don't know.
      st.pending.clear();
      syncPending();
      markStaleAndStop(ev);
      return;
    }
    const nextStep = st.steps[st.attempt];
    if (nextStep == null) {
      // Schedule exhausted with rows still pending. Verify rows become stale
      // (Vivid never took the price - the page says so); no-L rows just stop
      // (Vivid slower than the window; the next full read will show it).
      let moved = 0;
      for (const [key, p] of st.pending) {
        if (p.origin !== 'verify') continue;
        st.stale.set(key, {
          since: p.since, expectedListPrice: p.expectedListPrice,
          previousUnitPrice: p.previousUnitPrice, whatIf: p.whatIf,
        });
        moved++;
      }
      st.pending.clear();
      syncPending();
      if (moved) syncStale();
      markStaleAndStop(ev);
      return;
    }
    schedule(nextStep);
  }, [queryClient, schedule, syncPending, syncStale, markStaleAndStop]);
  tickRef.current = tick;

  /**
   * Start/extend polling for rows.
   * entries: [{ id, expectedListPrice, vividListingId?, previousUnitPrice?, whatIf? }]
   * origin 'write' (default): we just wrote these prices on rows with no L yet -
   *   (re)start the write schedule from step 0 and overwrite expectedListPrice
   *   for re-tracked ids.
   * origin 'verify': we just repriced rows that were already on Vivid -
   *   previousUnitPrice is the L before the write, whatIf Skybox's projection
   *   for the new B (either may be null). Runs the longer verify schedule.
   * origin 'rearm': rows found pending in a fetched payload - only adds ids not
   *   already tracked and never restarts an active session.
   */
  const track = useCallback((entries, { origin = 'write' } = {}) => {
    const st = s.current;
    if (!st.eventId) return;
    let added = 0;
    let staleChanged = false;
    const now = Date.now();
    for (const e of entries || []) {
      if (e == null || e.id == null) continue;
      const key = keyOf(e.id);
      const expected = e.expectedListPrice == null ? null : Number(e.expectedListPrice);
      if (expected !== null && !(expected > 0)) continue; // $0 = delisted, nothing to wait for
      if (origin === 'rearm' && st.pending.has(key)) continue;
      if (st.stale.delete(key)) staleChanged = true; // a new write supersedes an old warning
      let previousUnitPrice = null;
      let whatIf = null;
      if (origin === 'verify') {
        previousUnitPrice = e.previousUnitPrice > 0 ? Number(e.previousUnitPrice) : null;
        whatIf = e.whatIf > 0 ? Number(e.whatIf) : null;
        // Same L expected as before (e.g. a $1 change Vivid rounds away): nothing to verify
        if (previousUnitPrice != null && whatIf != null && Math.abs(whatIf - previousUnitPrice) < PRICE_EPS) continue;
      }
      st.pending.set(key, {
        expectedListPrice: expected, since: now, origin,
        vividListingId: e.vividListingId || null, previousUnitPrice, whatIf,
      });
      added++;
    }
    if (staleChanged) syncStale();
    if (!added) return;
    const active = st.timer !== null || st.inFlight;
    if (origin !== 'rearm' || !active) {
      // A session with any verify row runs the long schedule; plain write /
      // rearm sessions keep their shorter ones.
      let hasVerify = false;
      for (const p of st.pending.values()) if (p.origin === 'verify') { hasVerify = true; break; }
      st.steps = hasVerify ? VERIFY_STEPS_MS : origin === 'write' ? WRITE_STEPS_MS : REARM_STEPS_MS;
      st.attempt = 0;
      st.errors = 0;
      if (!st.inFlight) schedule(st.steps[0]);
    }
    syncPending();
  }, [schedule, syncPending, syncStale]);

  /** Remember Skybox's what-if L for a B we just wrote (see pins). */
  const pin = useCallback((id, unitPrice, listPrice) => {
    if (!(unitPrice > 0)) return;
    pins.current.set(keyOf(id), { unitPrice, listPrice: Number(listPrice) });
  }, []);

  /**
   * Effective L for rendering/sorting: the cache row's own value when it has
   * one and the row is not under verification, else a pinned value whose B
   * still matches the row's B, else the raw (0/null) value. A verifying or
   * stale row keeps showing the pinned projection even after a full refetch
   * puts Vivid's old L back into the cache - that is exactly the case the
   * marker in the cell explains.
   */
  const overlay = useCallback((listing) => {
    const key = keyOf(listing.id);
    const p = pins.current.get(key);
    const pinMatches = !!p && Math.abs((Number(listing.listPrice) || 0) - p.listPrice) < PRICE_EPS;
    const st = s.current;
    const underVerify = st.pending.get(key)?.origin === 'verify' || st.stale.has(key);
    if (listing.unitPrice > 0 && !(underVerify && pinMatches)) return { unitPrice: listing.unitPrice, pinned: false };
    if (pinMatches) return { unitPrice: p.unitPrice, pinned: true };
    return { unitPrice: listing.unitPrice, pinned: false };
  }, []);

  /**
   * Verification status of a row for the L cell: { status: 'verifying' | 'stale',
   * since, expectedListPrice, previousUnitPrice, whatIf } or null.
   */
  const verifyState = useCallback((id) => {
    const key = keyOf(id);
    const st = s.current;
    const p = st.pending.get(key);
    if (p && p.origin === 'verify') return { status: 'verifying', ...p };
    const info = st.stale.get(key);
    if (info) return { status: 'stale', ...info };
    return null;
  }, []);

  /**
   * Re-arm rule: after any full payload, track own rows that are priced but
   * have no L yet. Also drops a stale warning whose B has since changed (a
   * write we did not make). Vivid's L is deliberately NOT judged from the
   * cache here: Compare patches the what-if into the cache row itself, so
   * only a fresh /lprices read (tick) can clear a stale row on the merits.
   */
  const rearmFrom = useCallback((myInventory) => {
    if (!Array.isArray(myInventory)) return;
    const st = s.current;
    const entries = [];
    let staleChanged = false;
    for (const l of myInventory) {
      const info = st.stale.get(keyOf(l.id));
      if (info && Math.abs((Number(l.listPrice) || 0) - info.expectedListPrice) >= PRICE_EPS) {
        st.stale.delete(keyOf(l.id));
        staleChanged = true;
      }
      if (l.listPrice > 0 && !(l.unitPrice > 0)) {
        entries.push({ id: l.id, expectedListPrice: l.listPrice, vividListingId: l.vividListingId });
      }
    }
    if (staleChanged) syncStale();
    if (entries.length) track(entries, { origin: 'rearm' });
  }, [track, syncStale]);

  /**
   * Poll right now (Refresh button) if anything is pending. Stale rows get
   * one more short verify schedule so a "not on Vivid yet" warning can clear
   * itself once Skybox catches up.
   */
  const pollNow = useCallback(() => {
    const st = s.current;
    if (st.stale.size) {
      for (const [key, info] of st.stale) {
        st.pending.set(key, { ...info, origin: 'verify', vividListingId: null });
      }
      st.stale.clear();
      syncStale();
      syncPending();
      if (st.timer === null && !st.inFlight) {
        st.steps = REARM_STEPS_MS;
        st.attempt = 0;
        st.errors = 0;
      }
    }
    if (st.pending.size === 0 || st.inFlight) return;
    schedule(0);
  }, [schedule, syncPending, syncStale]);

  // Session lifecycle per eventId
  useEffect(() => {
    const st = s.current;
    st.eventId = eventId || null;
    st.gen += 1;
    st.pending = new Map();
    st.stale = new Map();
    st.attempt = 0;
    st.errors = 0;
    st.inFlight = false;
    pins.current = new Map();
    clearTimer();
    setPendingIds(new Set());
    setStaleIds(new Set());
    return () => {
      const hadPending = st.pending.size > 0;
      const ev = st.eventId;
      st.gen += 1;
      clearTimer();
      st.pending = new Map();
      st.stale = new Map();
      st.inFlight = false;
      if (hadPending && ev) {
        queryClient.invalidateQueries({ queryKey: eventPricingKeys.detail(ev), refetchType: 'none' });
      }
    };
  }, [eventId, queryClient]);

  return { track, pin, overlay, verifyState, rearmFrom, pollNow, pendingIds, staleIds };
}
